'use client';

import React, { createContext, useContext, useState, useRef, useCallback, ReactNode } from 'react';

// Global compose state coordinating two different compose pathways:
//
// 1. OmniBox Pathway (doc pages only - cross-component coordination):
//    - User clicks OmniBox → calls triggerComposeAtLatest()
//    - page.tsx has registered a callback that calls setReplyToDocKey(latestDocKey)
//    - Target DocCard's effect sees replyToDocKey match → opens compose box
//    - Effect clears replyToDocKey back to null (prevents re-triggering)
//
// 2. Inline Reply Pathway (any message - local state only):
//    - User clicks Reply button → manages local isComposeVisible state directly
//    - Does NOT use replyToDocKey (avoids cross-component coordination overhead)
//
// Note: isComposing is a simple boolean UI state flag (not used for routing).
// It signals to other components (LayoutWrapper, LiveContext) that a compose box is open.
//
interface ComposeContextType {
  isComposing: boolean;
  setIsComposing: (composing: boolean) => void;
  replyToDocKey: string | null;
  setReplyToDocKey: (docKey: string | null) => void;
  setOnComposeAtLatest: (callback: (() => void) | null) => void;
  triggerComposeAtLatest: () => void;
}

const ComposeContext = createContext<ComposeContextType | undefined>(undefined);

export const ComposeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isComposing, setIsComposing] = useState(false);
  const [replyToDocKey, setReplyToDocKey] = useState<string | null>(null);
  const onComposeAtLatestRef = useRef<(() => void) | null>(null);

  const setOnComposeAtLatest = useCallback((callback: (() => void) | null) => {
    onComposeAtLatestRef.current = callback;
  }, []);

  const triggerComposeAtLatest = useCallback(() => {
    if (onComposeAtLatestRef.current) {
      onComposeAtLatestRef.current();
    }
  }, []);

  return (
    <ComposeContext.Provider
      value={{
        isComposing,
        setIsComposing,
        replyToDocKey,
        setReplyToDocKey,
        setOnComposeAtLatest,
        triggerComposeAtLatest,
      }}
    >
      {children}
    </ComposeContext.Provider>
  );
};

export const useCompose = () => {
  const context = useContext(ComposeContext);
  if (context === undefined) {
    throw new Error('useCompose must be used within a ComposeProvider');
  }
  return context;
};
